import React from "react";
import { Play, Youtube, Instagram, Music2, Twitter, Lock, Clock, CheckCircle2 } from "lucide-react";
import { money, fmtViews } from "@/lib/format";
import { fileUrl } from "@/lib/api";

export function Logo({ size = 30, brandBg }) {
  return (
    <div className="flex items-center gap-2 select-none">
      <div
        className={brandBg ? "" : "brand-gradient"}
        style={{
          width: size, height: size, borderRadius: 8,
          display: "flex", alignItems: "center", justifyContent: "center",
          backgroundImage: brandBg ? `url(${fileUrl(brandBg)})` : undefined,
          backgroundSize: "cover",
        }}
      >
        <span style={{ color: "#fff", fontWeight: 500, fontSize: size * 0.5 }}>C</span>
      </div>
      <span style={{ fontWeight: 500, fontSize: 17, color: "var(--text)" }}>Clipup</span>
    </div>
  );
}

const PLAT_ICON = { youtube: Youtube, instagram: Instagram, tiktok: Music2, x: Twitter };

export function PlatformPill({ platform, label }) {
  const Icon = PLAT_ICON[platform] || Play;
  return (
    <span className="chip" data-testid={`platform-pill-${platform}`}>
      <Icon size={12} /> {label || platform}
    </span>
  );
}

export function StatusChip({ status }) {
  const map = {
    approved: { c: "var(--success)", t: "Approved", icon: CheckCircle2 },
    needs_review: { c: "var(--warning)", t: "Needs review", icon: Clock },
    rejected: { c: "var(--danger)", t: "Rejected", icon: null },
    active: { c: "var(--success)", t: "Active", icon: null },
    pending: { c: "var(--warning)", t: "Requested", icon: Clock },
    paid: { c: "var(--success)", t: "Paid", icon: null },
    eligible: { c: "var(--accent-light)", t: "Eligible", icon: null },
    on_hold: { c: "var(--warning)", t: "On hold", icon: null },
    unpaid: { c: "var(--muted)", t: "Unpaid", icon: null },
    partly_paid: { c: "var(--warning)", t: "Partly paid", icon: null },
  };
  const m = map[status] || { c: "var(--sub)", t: status };
  return (
    <span className="pill" style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "3px 10px", fontSize: 12, background: "rgba(255,255,255,.05)", border: "1px solid var(--border)", color: m.c }}>
      <span style={{ width: 7, height: 7, borderRadius: 999, background: m.c }} /> {m.t}
    </span>
  );
}

export function BudgetBar({ used, total, showLabel = true }) {
  const pct = total ? Math.min(100, (used / total) * 100) : 0;
  return (
    <div>
      <div className="progress-track"><div className="progress-fill" style={{ width: `${pct}%` }} /></div>
      {showLabel && (
        <div style={{ fontSize: 12, color: "var(--sub)", marginTop: 6 }}>
          {money(used)} of {money(total)}
        </div>
      )}
    </div>
  );
}

export function EndingSoon() {
  return <span className="pill" style={{ padding: "3px 9px", fontSize: 11, whiteSpace: "nowrap", flexShrink: 0, lineHeight: 1.3, background: "rgba(245,165,36,.12)", border: "1px solid rgba(245,165,36,.3)", color: "var(--warning)" }}>Ending soon</span>;
}

export function PosterBox({ poster, alt, rounded = 12, children, className = "", style = {} }) {
  const url = poster ? fileUrl(poster.url) : null;
  return (
    <div className={`${url ? "" : "brand-gradient"} ${className}`} style={{ borderRadius: rounded, overflow: "hidden", position: "relative", backgroundImage: url ? `url(${url})` : undefined, backgroundSize: "cover", backgroundPosition: poster?.focal ? `${poster.focal.x * 100}% ${poster.focal.y * 100}%` : "center", ...style }} aria-label={alt}>
      {children}
    </div>
  );
}

export function PhonePlaceholders() {
  const views = ["842K", "1.2M", "560K"];
  return (
    <div style={{ display: "flex", gap: 10, alignItems: "flex-end" }}>
      {views.map((v, i) => (
        <div key={i} style={{ width: 62, height: 110 + i % 2 * 18, borderRadius: 12, background: "rgba(255,255,255,.06)", border: "1px solid var(--border-strong)", display: "flex", alignItems: "flex-end", justifyContent: "center", paddingBottom: 8, backdropFilter: "blur(8px)" }}>
          <span style={{ fontSize: 11, color: "var(--banner-sub)" }}>{fmtViews ? v : v}</span>
        </div>
      ))}
    </div>
  );
}
